/**
 * 后台 V2 — 客服管理
 *
 * 功能:
 *   - 工单列表(状态/优先级/分类筛选 + 搜索)
 *   - 工单详情(用户信息 + 消息流)
 *   - 客服回复
 *   - 关闭工单
 *   - 工单统计
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Headphones, Search, Filter, MessageSquare, X, Send, Loader2,
  ChevronRight, ChevronLeft, Star, Phone, Mail, User as UserIcon,
  AlertCircle, CheckCircle2, Clock, XCircle, ArrowLeft, Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

const STATUS_LABELS = { open: '待处理', pending: '已回复', resolved: '已解决', closed: '已关闭' };
const STATUS_COLORS = {
  open: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  pending: 'text-brand-cyan bg-brand-cyan/10 border-brand-cyan/30',
  resolved: 'text-brand-gold bg-brand-gold/10 border-brand-gold/30',
  closed: 'text-ink-3 bg-white/5 border-white/10',
};
const PRIORITY_LABELS = { low: '低', normal: '普通', high: '高', urgent: '紧急' };
const PRIORITY_COLORS = {
  low: 'text-ink-2 bg-white/5',
  normal: 'text-brand-cyan bg-brand-cyan/10',
  high: 'text-amber-400 bg-amber-500/10',
  urgent: 'text-accent-danger bg-accent-danger/10',
};
const CATEGORIES = ['账户', '会员', '推荐', '支付', '产品', '技术', '退款', '其他'];

export function SupportView({ openTicket, onTicketOpened }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    load();
    loadStats();
  }, [filterStatus, filterCategory]);

  // 接收从 PushView 跳过来的工单 ID,自动打开
  useEffect(() => {
    if (openTicket) {
      setSelectedTicket(openTicket);
      onTicketOpened?.();
    }
  }, [openTicket]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCategory !== 'all') params.category = filterCategory;
      const res = await api.admin.support.tickets(params);
      if (res?.ok) setTickets(res.tickets || []);
    } catch (e) {
      toast.error('加载工单失败');
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const res = await api.admin.support.stats();
      if (res?.ok) setStats(res);
    } catch (e) {}
  };

  const filtered = useMemo(() => {
    if (!search) return tickets;
    const kw = search.toLowerCase();
    return tickets.filter(t =>
      String(t.id).includes(kw) ||
      (t.subject || '').toLowerCase().includes(kw) ||
      (t.user_phone || '').includes(kw) ||
      (t.user_name || '').toLowerCase().includes(kw)
    );
  }, [tickets, search]);

  // 批量清空已关闭工单
  const handleClearClosed = async () => {
    const closedCount = tickets.filter(t => t.status === 'closed').length;
    if (closedCount === 0) {
      toast.error('没有已关闭的工单');
      return;
    }
    if (!confirm(`确认删除全部 ${closedCount} 个已关闭工单?此操作不可恢复`)) return;
    try {
      const r = await api.admin.support.ticketBulkDelete({ status: 'closed' });
      if (r?.ok) {
        toast.success(r.message || `已删除 ${r.deleted} 个工单`);
        load();
        loadStats();
      } else {
        toast.error(r?.error || '删除失败');
      }
    } catch (e) { toast.error('删除失败'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold flex items-center gap-2" style={{ color: 'var(--ink-0)' }}>
          <Headphones size={16} className="text-brand-cyan" />
          客服工单
        </h2>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleClearClosed}
            className="text-[10px] text-ink-3 hover:text-red-400 flex items-center gap-1 px-2 py-1 rounded border border-white/5 hover:border-red-400/30"
            title="删除全部已关闭的工单"
          >
            <Trash2 size={11} /> 清空已关闭
          </button>
          <button onClick={load} className="btn-ghost text-[10px]">↻ 刷新</button>
        </div>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <StatCard label="总工单" value={stats.total} color="text-ink-1" />
          <StatCard label="待处理" value={stats.open} color="text-amber-400" />
          <StatCard label="已回复" value={stats.pending} color="text-brand-cyan" />
          <StatCard label="今日新" value={stats.today} color="text-brand-gold" />
        </div>
      )}

      {/* 筛选 + 搜索 */}
      <div className="card !p-2 space-y-1.5">
        <div className="flex items-center gap-2">
          <Search size={12} style={{ color: 'var(--ink-2)' }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="搜索工单 ID / 主题 / 手机号 / 用户名"
            className="flex-1 bg-transparent outline-none text-xs"
            style={{ color: 'var(--ink-0)' }}
          />
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="rounded text-xs px-2 py-1.5 outline-none"
            style={{ backgroundColor: 'var(--bg-elev)', color: 'var(--ink-0)', border: '1px solid var(--border-soft)' }}
          >
            <option value="all">所有状态</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="rounded text-xs px-2 py-1.5 outline-none"
            style={{ backgroundColor: 'var(--bg-elev)', color: 'var(--ink-0)', border: '1px solid var(--border-soft)' }}
          >
            <option value="all">所有分类</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* 工单列表 */}
      {loading ? (
        <div className="card text-center py-8 text-ink-2 text-xs">
          <Loader2 size={20} className="mx-auto mb-2 animate-spin" />
          加载中...
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-8 text-ink-3 text-xs">
          <MessageSquare size={28} className="mx-auto mb-2 opacity-30" />
          没有工单
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map(t => (
            <TicketRow
              key={t.id}
              ticket={t}
              expanded={selectedTicket === t.id}
              onToggle={() => setSelectedTicket(prev => prev === t.id ? null : t.id)}
              onReplied={() => { load(); loadStats(); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="card !p-2 text-center">
      <div className="text-[10px] text-ink-3">{label}</div>
      <div className={clsx('text-xl font-bold', color)}>{value ?? '-'}</div>
    </div>
  );
}

// 工单行:列表项 + 行内展开 thread + 1 个回复框(无弹窗)
// 关键改动:发送走"乐观更新"路径 —— 立即把新消息插到本地,不再 reload
// 这样避免了 "setLoading(true) → spinner → 几十ms 后 setLoading(false)" 的"缓冲关闭再打开"感
function TicketRow({ ticket: t, expanded, onToggle, onReplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  // 本地消息列表(初始为后端拉到的,后续乐观插入/失败回滚都在这里)
  const [localMsgs, setLocalMsgs] = useState([]);
  const isClosed = t.status === 'closed';
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (expanded && !data) {
      loadDetail();
    }
  }, [expanded]);

  // 消息列表更新(乐观插入 / loadDetail 完成) → 自动滚到底部
  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [localMsgs.length, expanded]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const r = await api.admin.support.ticketDetail(t.id);
      if (r?.ok) {
        setData(r);
        setLocalMsgs(r.messages || []);
      }
    } catch (e) {}
    setLoading(false);
  };

  const handleReply = async () => {
    const content = reply.trim();
    if (!content) return;
    if (content.length > 5000) {
      toast.error('内容过长(≤5000 字符)');
      return;
    }
    setSending(true);

    // 1) 乐观插入 —— 立即让新消息出现,textarea 立即清空
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const tempMsg = {
      id: tempId,
      sender_type: 'admin',
      content,
      created_at: Math.floor(Date.now() / 1000),
      _pending: true,
    };
    setLocalMsgs(prev => [...prev, tempMsg]);
    setReply('');

    try {
      const r = await api.admin.support.ticketReply(t.id, content);
      if (r?.ok) {
        // 2) 后端确认:temp → 已确认(去掉 _pending 标记,id 留给后端真实值,
        //    下次 loadDetail 时会被真实消息覆盖)
        setLocalMsgs(prev => prev.map(m => m.id === tempId ? { ...m, _pending: false } : m));
        toast.success('已回复');
        // 3) 通知父级更新 message_count + status(异步,不影响当前 row 视觉)
        onReplied?.();
      } else {
        // 失败:回滚 + 恢复输入
        setLocalMsgs(prev => prev.filter(m => m.id !== tempId));
        setReply(content);
        toast.error(r?.error || '回复失败');
      }
    } catch (e) {
      setLocalMsgs(prev => prev.filter(m => m.id !== tempId));
      setReply(content);
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!confirm('确认关闭工单 #' + t.id + '?')) return;
    try {
      const r = await api.admin.support.ticketClose(t.id);
      if (r?.ok) {
        toast.success('工单已关闭');
        // 关闭后必须 reload 一次拿最新 messages(rating 可能变化)
        await loadDetail();
        onReplied?.();
      }
    } catch (e) { toast.error(e.message); }
  };

  // 删除工单(已关闭的才允许)
  const handleDelete = async (e) => {
    e?.stopPropagation();
    if (!isClosed) {
      toast.error('只能删除已关闭的工单');
      return;
    }
    if (!confirm(`确认删除工单 #${t.id}?所有消息和评分将一并删除,不可恢复`)) return;
    try {
      const r = await api.admin.support.ticketDelete(t.id);
      if (r?.ok) {
        toast.success('工单已删除');
        onReplied?.();  // 触发父组件 reload
      } else {
        toast.error(r?.error || '删除失败');
      }
    } catch (err) { toast.error(err.message); }
  };

  const allMsgs = localMsgs;

  return (
    <div className="card !p-2.5">
      {/* 列表行 */}
      <button
        onClick={onToggle}
        className="w-full text-left hover:opacity-80 transition"
      >
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-ink-2">
            {t.category}
          </span>
          <span className={clsx(
            'text-[10px] px-1.5 py-0.5 rounded border',
            PRIORITY_COLORS[t.priority]
          )}>
            {PRIORITY_LABELS[t.priority]}
          </span>
          <span className={clsx(
            'text-[10px] px-1.5 py-0.5 rounded border',
            STATUS_COLORS[t.status]
          )}>
            {STATUS_LABELS[t.status]}
          </span>
          <span className="text-[10px] text-ink-3 ml-auto">#{t.id}</span>
        </div>
        <div className="text-xs font-semibold line-clamp-1 mb-0.5">{t.subject}</div>
        <div className="flex items-center gap-2 text-[10px] text-ink-3">
          <span>{t.user_name || t.user_phone || '用户'}</span>
          <span>·</span>
          <span>{t.message_count || 0} 条</span>
          <span>·</span>
          <span>{fmtTime(t.last_reply_at || t.created_at)}</span>
          <span className="ml-auto">{expanded ? '收起' : '展开'}</span>
          {isClosed && (
            <button
              onClick={handleDelete}
              className="text-ink-3 hover:text-red-400 p-1 rounded hover:bg-red-400/10"
              title="删除此工单(已关闭)"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </button>

      {/* 行内展开:对话 + 回复框(无弹窗) */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-2 pt-2 border-t border-white/5">
              {loading ? (
                <div className="text-center py-3 text-ink-3 text-xs">
                  <Loader2 size={14} className="inline animate-spin mr-1" /> 加载对话...
                </div>
              ) : allMsgs.length === 0 ? (
                <div className="text-center py-3 text-ink-3 text-xs">暂无消息</div>
              ) : (
                <div ref={scrollerRef} className="bg-bg-elev/50 rounded-lg p-2 mb-2 max-h-72 overflow-y-auto space-y-1.5">
                  {allMsgs.map(msg => {
                    const isUser = msg.sender_type === 'user';
                    const isPending = msg._pending === true;
                    return (
                      <div key={msg.id} className={clsx('flex', isUser ? 'justify-start' : 'justify-end')}>
                        <div
                          className={clsx(
                            'max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs transition-opacity',
                            isUser
                              ? 'bg-white/5 text-ink-1 border border-white/5'
                              : 'bg-brand-cyan/15 text-ink-0 border border-brand-cyan/30',
                            isPending && 'opacity-60'
                          )}
                        >
                          <div className="text-[9px] text-ink-3 mb-0.5 flex items-center gap-1">
                            <span>{isUser ? '👤 用户' : '✉️ 客服'}</span>
                            <span>·</span>
                            <span>{new Date(msg.created_at * 1000).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                            {isPending && (
                              <span className="text-brand-cyan/80 flex items-center gap-0.5 ml-1">
                                <Loader2 size={8} className="animate-spin" /> 发送中
                              </span>
                            )}
                          </div>
                          <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 单一回复框 */}
              {!isClosed && (
                <div className="bg-bg-elev border border-white/10 rounded-lg p-2">
                  <textarea
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    placeholder={`回复工单 #${t.id}...`}
                    rows={2}
                    maxLength={5000}
                    className="w-full bg-bg-card border border-white/10 rounded px-2 py-1.5 text-xs outline-none focus:border-brand-cyan/40 resize-none"
                    style={{ color: 'var(--ink-0)' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleReply();
                      }
                    }}
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[10px] text-ink-3">{reply.length}/5000 · Enter 发送</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={handleClose}
                        className="text-[10px] text-ink-3 hover:text-red-400 px-2 py-1"
                      >
                        关闭工单
                      </button>
                      <button
                        onClick={handleReply}
                        disabled={!reply.trim() || sending}
                        className="btn-primary !text-[10px] !py-1 !px-2 flex items-center gap-1 disabled:opacity-50"
                      >
                        {sending ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                        发送
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TicketDetailPanel({ ticketId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    load();
  }, [ticketId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.admin.support.ticketDetail(ticketId);
      if (res?.ok) setData(res);
    } catch (e) {}
    setLoading(false);
  };

  const handleReply = async () => {
    if (!reply.trim()) {
      toast.error('请输入回复');
      return;
    }
    setSending(true);
    try {
      const res = await api.admin.support.ticketReply(ticketId, reply);
      if (res?.ok) {
        setReply('');
        toast.success('已回复');
        load();
      } else {
        toast.error(res?.error || '回复失败');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setSending(false);
  };

  const handleClose = async () => {
    if (!confirm('确认关闭工单?')) return;
    try {
      const res = await api.admin.support.ticketClose(ticketId);
      if (res?.ok) {
        toast.success('已关闭');
        load();
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center"
        onClick={onClose}
      >
        <Loader2 size={32} className="text-brand-cyan animate-spin" />
      </motion.div>
    );
  }

  if (!data) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center"
        onClick={onClose}
      >
        <div className="card text-center py-6 text-xs text-ink-2">
          工单不存在
        </div>
      </motion.div>
    );
  }

  const { ticket, messages = [], rating } = data;
  const isClosed = ticket.status === 'closed';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch md:items-center justify-center md:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        className="w-full md:max-w-2xl bg-bg-2 rounded-t-2xl md:rounded-2xl border border-white/10 flex flex-col max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-3 border-b border-white/5 flex items-center gap-2">
          <button onClick={onClose} className="text-ink-3 hover:text-ink-0">
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-ink-2">
                {ticket.category}
              </span>
              <span className={clsx(
                'text-[10px] px-1.5 py-0.5 rounded border',
                PRIORITY_COLORS[ticket.priority]
              )}>
                {PRIORITY_LABELS[ticket.priority]}
              </span>
              <span className={clsx(
                'text-[10px] px-1.5 py-0.5 rounded border',
                STATUS_COLORS[ticket.status]
              )}>
                {STATUS_LABELS[ticket.status]}
              </span>
              <span className="text-[10px] text-ink-3">#{ticket.id}</span>
            </div>
            <div className="text-sm font-bold line-clamp-1">{ticket.subject}</div>
          </div>
          {!isClosed && (
            <button onClick={handleClose} className="text-[10px] text-ink-3 hover:text-accent-danger">
              关闭
            </button>
          )}
        </div>

        {/* 用户信息 */}
        <div className="p-2.5 border-b border-white/5 bg-white/3">
          <div className="flex items-center gap-2 text-xs">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-cyan to-brand-purple flex items-center justify-center text-white text-[10px] font-bold">
              {(ticket.user_name || ticket.user_phone || '?').slice(-2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">
                {ticket.user_name || `用户${(ticket.user_phone || '').slice(-4)}`}
              </div>
              <div className="text-[10px] text-ink-3 flex items-center gap-2">
                <span>📱 {ticket.user_phone}</span>
                <span>·</span>
                <span className={clsx(
                  'px-1.5 rounded',
                  ticket.user_tier === 'MAX 买断' ? 'bg-brand-gold/20 text-brand-gold' :
                  ticket.user_tier === 'Pro 年付' ? 'bg-brand-purple/20 text-brand-purple' :
                  'bg-white/5 text-ink-2'
                )}>
                  {ticket.user_tier || '试用'}
                </span>
                {ticket.total_commission > 0 && (
                  <>
                    <span>·</span>
                    <span>佣金 ¥{ticket.total_commission}</span>
                  </>
                )}
              </div>
            </div>
            <div className="text-[10px] text-ink-3 text-right">
              <div>{fmtTime(ticket.created_at)}</div>
              <div>提交</div>
            </div>
          </div>
        </div>

        {/* 消息流 */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {messages.map(m => (
            <div
              key={m.id}
              className={clsx(
                'flex',
                m.sender_type === 'admin' ? 'justify-end' : 'justify-start'
              )}
            >
              <div className={clsx(
                'max-w-[80%] rounded-2xl px-3 py-2 text-xs',
                m.sender_type === 'admin'
                  ? 'bg-brand-cyan/20 border border-brand-cyan/30 text-ink-0'
                  : 'bg-white/5 border border-white/10 text-ink-1'
              )}>
                <div className="flex items-center gap-1.5 mb-1 text-[10px] text-ink-3">
                  <span className="font-semibold">
                    {m.sender_type === 'admin' ? '🛡 客服' : '👤 用户'}
                  </span>
                  <span>·</span>
                  <span>{fmtTime(m.created_at)}</span>
                </div>
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
              </div>
            </div>
          ))}
        </div>

        {/* 评价(已关闭) */}
        {rating && (
          <div className="p-2.5 border-t border-white/5 bg-white/3 text-center text-xs">
            <div className="text-ink-3 mb-1">用户评价</div>
            <div className="flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map(i => (
                <Star
                  key={i}
                  size={14}
                  className={i <= rating.rating ? 'text-brand-gold fill-brand-gold' : 'text-ink-3'}
                />
              ))}
            </div>
            {rating.comment && (
              <div className="text-ink-2 mt-1 text-[10px]">{rating.comment}</div>
            )}
          </div>
        )}

        {/* 回复输入 */}
        {!isClosed ? (
          <div className="p-2.5 border-t border-white/5">
            <textarea
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder="输入回复..."
              rows={3}
              maxLength={5000}
              className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-brand-cyan/40 resize-none"
            />
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[10px] text-ink-3">{reply.length}/5000</span>
              <div className="flex gap-1.5">
                <button
                  onClick={() => {
                    setReply('您好,您的问题已收到,我们会尽快处理。');
                    toast.success('已填入快捷回复');
                  }}
                  className="text-[10px] text-ink-3 hover:text-ink-1"
                >
                  📝 快捷回复
                </button>
                <button
                  onClick={handleReply}
                  disabled={!reply.trim() || sending}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-[11px] font-semibold flex items-center gap-1.5',
                    reply.trim() && !sending
                      ? 'btn-primary'
                      : 'bg-white/5 text-ink-3 cursor-not-allowed'
                  )}
                >
                  {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                  发送
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-3 border-t border-white/5 text-center text-[10px] text-ink-3">
            <XCircle size={16} className="inline mr-1" />
            工单已关闭
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function fmtTime(ts) {
  if (!ts) return '-';
  const d = new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return '刚刚';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)} 天前`;
  return d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
}
